import { SEND_UNDO_WINDOW_MS } from '@/features/send-undo';
import {
  MAX_MESSAGE_ATTACHMENT_BYTES,
  MAX_OUTBOX_BYTES,
  MAX_OUTBOX_MESSAGES,
  outboxMessageBytes,
  type OutboxDraft,
  type OutboxFailure,
  type OutboxMessage,
} from './types';
import { outboxStorage, outboxStorageSupported } from './storage';

/**
 * The Outbox store: the durable queue, and the one snapshot the UI reads.
 *
 * Everything that changes a record goes through here, so there is exactly one
 * place that (a) writes IndexedDB, (b) refreshes the in-memory snapshot and
 * (c) tells the other tabs. The flusher (`flush.ts`) and the views are both
 * ordinary callers.
 *
 * ## Snapshot, not a query
 *
 * The UI reads through `useSyncExternalStore`, so the snapshot is a plain array
 * that is replaced (never mutated) on every change. React Query is not used:
 * this is client-owned state with no server to be the authority on it, and
 * putting it behind a query would invite a refetch to overwrite a local edit.
 */

const CHANNEL_NAME = 'ols-mail-outbox';

/** A lease older than this is presumed to belong to a tab that died. */
export const SENDING_LEASE_MS = 90_000;

/** First backoff step. Doubles per attempt, to {@link MAX_BACKOFF_MS}. */
export const BASE_BACKOFF_MS = 15_000;
export const MAX_BACKOFF_MS = 10 * 60_000;

/**
 * How long the undo window's hold lasts before {@link recoverOutbox} decides
 * the tab that made it is gone. Twice the window, so an ordinary busy tab is
 * never mistaken for a dead one.
 *
 * This is the hold for the DEFAULT window. The window is a preference (5–30 s),
 * so the composer passes `holdMs` for the one it is actually using
 * ({@link undoHoldMs}): a 10 s hold under a 30 s window would let the flusher
 * send the message while Undo was still on screen.
 */
export const UNDO_HOLD_MS = SEND_UNDO_WINDOW_MS * 2;

/** The durable hold for an undo window of `windowMs`. */
export function undoHoldMs(windowMs: number): number {
  return windowMs * 2;
}

/** How long an editing composer's hold lasts between refreshes. */
export const EDIT_HOLD_MS = 60_000;

/** This tab, for the `sending` lease. Not persisted; a reload is a new tab. */
export const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

let snapshot: readonly OutboxMessage[] = [];
const listeners = new Set<() => void>();
let loaded: Promise<void> | undefined;

/**
 * Whose Outbox this tab is showing: the signed-in owner's `account.id`, or
 * `null` with nobody signed in. Set by the session layer
 * ({@link setOutboxOwner}); every read filters on it.
 *
 * Why the queue is keyed by owner at all (ADR 0061 §4): a revoked session keeps
 * its unsent mail on the device so it sends after the same person signs back
 * in — and on a shared machine the next person to sign in may well be somebody
 * else, who must not see those recipients and bodies, let alone send them.
 */
let currentOwner: string | null = null;

/** A record belongs to the current owner, or predates owner stamping. */
function visibleToCurrentOwner(record: OutboxMessage): boolean {
  return record.ownerId === undefined || record.ownerId === currentOwner;
}

const channel =
  typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : undefined;

function emit(): void {
  for (const listener of listeners) listener();
}

/** Oldest first: the queue flushes in the order the user pressed Send. */
function byAge(a: OutboxMessage, b: OutboxMessage): number {
  return a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1);
}

async function reload(): Promise<void> {
  if (!outboxStorageSupported()) {
    snapshot = [];
    emit();
    return;
  }
  try {
    snapshot = (await outboxStorage().all()).filter(visibleToCurrentOwner).sort(byAge);
  } catch {
    // A database we cannot read is not a reason to take the app down; the
    // banner falls back to "offline" with no count, which is still true.
    snapshot = [];
  }
  emit();
}

/** Reload and tell the other tabs. Every mutation ends here. */
async function changed(broadcast = true): Promise<void> {
  await reload();
  if (broadcast) channel?.postMessage({ type: 'changed', from: TAB_ID });
}

if (channel !== undefined) {
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const data = event.data as { type?: string; from?: string } | null;
    if (data?.type !== 'changed' || data.from === TAB_ID) return;
    void reload();
  };
}

/** `useSyncExternalStore` pair. The first subscriber triggers the initial read. */
export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  loaded ??= reload();
  return () => {
    listeners.delete(listener);
  };
}

export function outboxSnapshot(): readonly OutboxMessage[] {
  return snapshot;
}

/** Loads the queue from disk. Idempotent; call it before the first flush. */
export function loadOutbox(): Promise<void> {
  loaded ??= reload();
  return loaded;
}

/** Forgets the cached snapshot. Tests only. */
export function resetOutboxStore(): void {
  snapshot = [];
  loaded = undefined;
  currentOwner = null;
}

/** Whose queue this tab shows and sends, `null` when nobody is signed in. */
export function outboxOwner(): string | null {
  return currentOwner;
}

/**
 * The session layer says who is signed in. Re-reads the queue through the new
 * owner's filter and wakes every subscriber — including the flusher, which is
 * how a kept queue goes out after its owner signs back in.
 */
export function setOutboxOwner(owner: string | null): Promise<void> {
  if (owner === currentOwner) return loaded ?? Promise.resolve();
  currentOwner = owner;
  // Drop the previous owner's rows from the UI at once, not after the read:
  // they must not stay drawn for a frame under somebody else's session.
  snapshot = snapshot.filter(visibleToCurrentOwner);
  emit();
  loaded = reload();
  return loaded;
}

/**
 * A revoked session keeps its Outbox (ADR 0061 §4): stamp every record that
 * has no owner yet with the one whose session just ended, so it stays theirs
 * — hidden from anyone else who signs in here — and then stop showing it.
 *
 * `owner` is `undefined` on a cold load whose very first whoami answered
 * `session_revoked`, when this tab never learned who was signed in. A legacy
 * record is then stamped with its own `accountId`: for mail written in the
 * primary mailbox that *is* the owner id (`Session.account.id` is the primary
 * mailbox's id), and for a connected mailbox it matches nobody, so the record
 * stays hidden rather than being shown to whoever signs in next. Records
 * queued since owner stamping carry their owner already and are untouched.
 */
export async function retainOutboxForOwner(owner: string | undefined): Promise<number> {
  let kept = 0;
  if (outboxStorageSupported()) {
    try {
      for (const record of await outboxStorage().all()) {
        if (record.ownerId === undefined) {
          await outboxStorage().mutate(record.id, (current) =>
            current.ownerId === undefined
              ? { ...current, ownerId: owner ?? current.accountId }
              : undefined,
          );
        }
        kept += 1;
      }
    } catch {
      // Unreadable: nothing the next person could be shown either.
    }
  }
  currentOwner = null;
  await changed();
  return kept;
}

/**
 * How many messages this device is holding for somebody to sign back in,
 * read from disk. The sign-in screen's notice; nobody is signed in, so no
 * owner filter applies and nothing about the messages but their number is
 * exposed.
 */
export async function heldForSignInCount(): Promise<number> {
  if (!outboxStorageSupported()) return 0;
  try {
    return (await outboxStorage().all()).filter((one) => one.ownerId !== undefined).length;
  } catch {
    return 0;
  }
}

/**
 * Drop the in-memory queue after the database underneath it has been deleted.
 *
 * `resetOutboxStore` is not enough here and is not a substitute: it forgets the
 * snapshot without telling anybody, so every mounted `useSyncExternalStore`
 * subscriber keeps rendering the array it was last handed — the folder badge,
 * the offline banner's count, and the Outbox view itself would go on showing
 * messages that no longer exist on disk, and the flusher would go on trying to
 * send them. This emits, so the UI re-reads an empty queue, and broadcasts, so
 * the other tabs reload rather than writing the deleted records back.
 *
 * Called by `purgeOutbox` (`./purge`) and nothing else.
 */
export function clearOutboxSnapshot(): void {
  snapshot = [];
  // `undefined`, not a resolved promise: a later sign-in on this same page
  // must be free to read whatever the database holds then.
  loaded = undefined;
  emit();
  channel?.postMessage({ type: 'changed', from: TAB_ID });
}

function newId(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (cryptoObj?.randomUUID !== undefined) return cryptoObj.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Why a message could not be queued, when it could not be.
 *
 * Thrown rather than returned so the composer cannot forget to check it: the
 * whole point of the cap is that the send is refused **while the composer is
 * still open**, with the message still in it, rather than accepted now and
 * discovered unsendable when the queue flushes.
 */
export class OutboxFullError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OutboxFullError';
  }
}

/** The copy shown when a cap is hit. Exported so a test pins it. */
export const OUTBOX_CAP_MESSAGES = {
  message:
    "This message's attachments are larger than the Outbox can hold while you're offline. " +
    'Remove an attachment, or wait until you have a connection to send it.',
  outbox:
    'The Outbox is full. Send or remove some of the messages already waiting, ' +
    'or wait until you have a connection.',
  count:
    'The Outbox is holding as many messages as it can. Send or remove some of the messages ' +
    'already waiting, or wait until you have a connection.',
} as const;

function currentBytes(): number {
  return snapshot.reduce((total, one) => total + outboxMessageBytes(one), 0);
}

/**
 * Writes a message to the Outbox.
 *
 * `state` is `held` by default: the record exists from the moment Send is
 * pressed, before the undo window opens, so a tab that dies mid-window leaves
 * the instruction behind rather than losing it (see {@link OutboxState}).
 *
 * Throws {@link OutboxFullError} when a cap would be crossed. Nothing is
 * written in that case.
 */
export async function enqueueOutbox(
  draft: OutboxDraft,
  options: { held?: boolean; now?: number; holdMs?: number } = {},
): Promise<OutboxMessage> {
  await loadOutbox();
  const now = options.now ?? Date.now();

  const size = outboxMessageBytes(draft);
  const attachmentBytes = draft.attachments.reduce((total, one) => total + one.sizeBytes, 0);
  if (attachmentBytes > MAX_MESSAGE_ATTACHMENT_BYTES) {
    throw new OutboxFullError(OUTBOX_CAP_MESSAGES.message);
  }
  if (snapshot.length >= MAX_OUTBOX_MESSAGES) {
    throw new OutboxFullError(OUTBOX_CAP_MESSAGES.count);
  }
  if (currentBytes() + size > MAX_OUTBOX_BYTES) {
    throw new OutboxFullError(OUTBOX_CAP_MESSAGES.outbox);
  }

  const held = options.held ?? true;
  const record: OutboxMessage = {
    ...draft,
    id: newId(),
    ...(currentOwner !== null ? { ownerId: currentOwner } : {}),
    idempotencyKey: newId(),
    state: held ? 'held' : 'queued',
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    lastAttemptAt: 0,
    nextAttemptAt: now,
    ...(held ? { holdUntil: now + (options.holdMs ?? UNDO_HOLD_MS) } : {}),
  };

  try {
    await outboxStorage().add(record);
  } catch (cause) {
    // A quota refusal is the other way a cap is hit, and the honest answer is
    // the same one: say so now, with the composer still open.
    throw new OutboxFullError(
      cause instanceof DOMException && cause.name === 'QuotaExceededError'
        ? OUTBOX_CAP_MESSAGES.outbox
        : 'This message could not be stored on this device, so it has not been queued.',
    );
  }
  await changed();
  return record;
}

/** The undo window closed without an Undo: the message may now be flushed. */
export async function releaseOutbox(id: string, now = Date.now()): Promise<void> {
  await outboxStorage().mutate(id, (current) =>
    current.state === 'held'
      ? { ...current, state: 'queued', updatedAt: now, nextAttemptAt: now, holdUntil: undefined }
      : undefined,
  );
  await changed();
}

/**
 * Holds a queued message while a composer has it open for editing.
 *
 * The record stays in the Outbox the whole time — visible, and still the user's
 * message — rather than being lifted out and put back. Lifting it out is the
 * version that loses it: an offline composer cannot autosave a draft either, so
 * for the length of the edit the message would exist in nothing but React
 * state, which is precisely the failure this feature exists to prevent.
 *
 * The hold carries a deadline that the open composer refreshes
 * ({@link touchOutboxHold}). A tab that dies mid-edit lets it lapse, and
 * {@link recoverOutbox} sends the message as it was.
 */
export async function holdOutboxForEdit(
  id: string,
  now = Date.now(),
): Promise<OutboxMessage | undefined> {
  const held = await outboxStorage().mutate(id, (current) =>
    current.state === 'queued' || current.state === 'failed'
      ? {
          ...current,
          state: 'held' as const,
          updatedAt: now,
          holdUntil: now + EDIT_HOLD_MS,
        }
      : undefined,
  );
  await changed();
  return held;
}

/** Extends an edit hold. Called on a timer while the composer is open. */
export async function touchOutboxHold(id: string, now = Date.now()): Promise<void> {
  await outboxStorage().mutate(id, (current) =>
    current.state === 'held' ? { ...current, holdUntil: now + EDIT_HOLD_MS } : undefined,
  );
  await changed();
}

/** Undo, or the user removing a message from the Outbox. */
export async function removeOutbox(id: string): Promise<void> {
  await outboxStorage().remove(id);
  await changed();
}

/**
 * Replaces a queued message's content after an edit.
 *
 * **A new idempotency key.** The body has changed, so the old key names a
 * different request; reusing it would earn `409 idempotency_key_reused` at
 * best and replay the *old* body at worst. Staged attachment ids are dropped
 * with it, so the next attempt re-stages from the bytes we still hold.
 */
export async function replaceOutbox(
  id: string,
  draft: OutboxDraft,
  now = Date.now(),
): Promise<OutboxMessage | undefined> {
  const next = await outboxStorage().mutate(id, (current) =>
    current.state === 'sending'
      ? undefined
      : {
          ...current,
          ...draft,
          attachments: draft.attachments.map(({ stagedId: _drop, ...rest }) => rest),
          idempotencyKey: newId(),
          state: 'queued' as const,
          updatedAt: now,
          attempts: 0,
          nextAttemptAt: now,
          holdUntil: undefined,
          failure: undefined,
        },
  );
  await changed();
  return next;
}

/** "Try again" on a failed message. */
export async function retryOutbox(id: string, now = Date.now()): Promise<void> {
  await outboxStorage().mutate(id, (current) =>
    current.state === 'failed'
      ? {
          ...current,
          state: 'queued',
          updatedAt: now,
          attempts: 0,
          nextAttemptAt: now,
          failure: undefined,
        }
      : undefined,
  );
  await changed();
}

/**
 * Takes the lease on a message, or returns undefined if another tab has it.
 *
 * The guard and the write are one IndexedDB transaction, which is what makes
 * this safe between tabs rather than merely unlikely to collide.
 */
export async function claimOutbox(
  id: string,
  now = Date.now(),
): Promise<OutboxMessage | undefined> {
  const claimed = await outboxStorage().mutate(id, (current) => {
    // Never another owner's mail, whatever the snapshot said: the check sits
    // inside the same transaction as the lease, so a sign-in that changed the
    // owner mid-flush cannot slip a previous user's message through.
    if (!visibleToCurrentOwner(current)) return undefined;
    const leaseExpired = now - current.updatedAt > SENDING_LEASE_MS;
    const claimable =
      current.state === 'queued' || (current.state === 'sending' && leaseExpired);
    if (!claimable) return undefined;
    if (current.nextAttemptAt > now) return undefined;
    return {
      ...current,
      state: 'sending' as const,
      claimedBy: TAB_ID,
      updatedAt: now,
      attempts: current.attempts + 1,
      lastAttemptAt: now,
    };
  });
  await changed();
  return claimed;
}

/** The send went. The record has done its job and goes away. */
export async function completeOutbox(id: string): Promise<void> {
  await outboxStorage().remove(id);
  await changed();
}

/** A retryable failure: back off and try again, keeping the same key. */
export async function deferOutbox(
  id: string,
  failure: OutboxFailure,
  now = Date.now(),
  /** The server's Retry-After, in ms: the next try is no sooner than this. */
  notBeforeMs?: number,
): Promise<void> {
  await outboxStorage().mutate(id, (current) => {
    const backoff = Math.min(BASE_BACKOFF_MS * 2 ** Math.max(0, current.attempts - 1), MAX_BACKOFF_MS);
    const step = Math.max(backoff, notBeforeMs ?? 0);
    return {
      ...current,
      state: 'queued' as const,
      claimedBy: undefined,
      updatedAt: now,
      nextAttemptAt: now + step,
      failure,
    };
  });
  await changed();
}

/** A permanent failure. It stops here, visibly, with the reason attached. */
export async function failOutbox(
  id: string,
  failure: OutboxFailure,
  now = Date.now(),
): Promise<void> {
  await outboxStorage().mutate(id, (current) => ({
    ...current,
    state: 'failed' as const,
    claimedBy: undefined,
    updatedAt: now,
    failure,
  }));
  await changed();
}

/** Records the staged ids so a retry sends a byte-identical body. */
export async function recordStagedIds(
  id: string,
  stagedIds: (string | undefined)[],
): Promise<void> {
  await outboxStorage().mutate(id, (current) => ({
    ...current,
    attachments: current.attachments.map((attachment, index) => {
      const stagedId = stagedIds[index];
      return stagedId === undefined ? attachment : { ...attachment, stagedId };
    }),
  }));
  await changed();
}

/**
 * Repairs the queue after a tab died, and is the reason commit-on-unload still
 * means "send" when the app is offline.
 *
 * - A `held` record whose undo window has run out belongs to a tab that was
 *   closed mid-window. `pendingSend.ts` commits on unload because the user
 *   pressed Send and Undo is the only thing that means "don't"; the same rule
 *   applies here, so the record is promoted rather than dropped.
 * - A `sending` lease older than {@link SENDING_LEASE_MS} belongs to a tab that
 *   is gone. It goes back on the queue — safely, because the idempotency key
 *   travelled with the record, so if that dead tab's request did reach the
 *   server the replay returns the same answer instead of sending twice.
 */
export async function recoverOutbox(now = Date.now()): Promise<void> {
  await loadOutbox();
  let touched = false;
  for (const record of snapshot) {
    if (record.state === 'held' && now > (record.holdUntil ?? record.createdAt + UNDO_HOLD_MS)) {
      await outboxStorage().mutate(record.id, (current) =>
        current.state === 'held' && now > (current.holdUntil ?? current.createdAt + UNDO_HOLD_MS)
          ? {
              ...current,
              state: 'queued',
              updatedAt: now,
              nextAttemptAt: now,
              holdUntil: undefined,
            }
          : undefined,
      );
      touched = true;
    } else if (
      record.state === 'sending' &&
      record.claimedBy !== TAB_ID &&
      now - record.updatedAt > SENDING_LEASE_MS
    ) {
      await outboxStorage().mutate(record.id, (current) =>
        current.state === 'sending'
          ? { ...current, state: 'queued', claimedBy: undefined, updatedAt: now }
          : undefined,
      );
      touched = true;
    }
  }
  if (touched) await changed();
}

/** Messages on their way out: everything but a permanent failure. */
export function waitingCount(messages: readonly OutboxMessage[] = snapshot): number {
  return messages.filter((one) => one.state !== 'failed').length;
}

export function failedCount(messages: readonly OutboxMessage[] = snapshot): number {
  return messages.filter((one) => one.state === 'failed').length;
}

/** The next message due, oldest first. Undefined when nothing is due yet. */
export function nextDue(
  now = Date.now(),
  messages: readonly OutboxMessage[] = snapshot,
): OutboxMessage | undefined {
  return messages.find(
    (one) =>
      (one.state === 'queued' || (one.state === 'sending' && now - one.updatedAt > SENDING_LEASE_MS)) &&
      one.nextAttemptAt <= now,
  );
}
